/**
 * Unpacks an archive the person picked or dropped, in the browser, into the same `{ file, relPath, size }` entries a
 * folder gives (collectEntries.js): the upload then packs and sends them as it does any folder, with its layout,
 * skips, progress and Cancel. Zip (fflate) and tar / tar.gz (a small reader below); anything else is refused with a
 * clear message.
 *
 * Large archives stay responsive: a zip is unpacked a batch of entries at a time and a tar.gz is gunzipped a chunk at a
 * time, the page repainting (progress) and Cancel being honoured between them. Entries the caller does not want
 * (`skip`) are never decompressed. Links, devices and the like are never unpacked; paths are checked again by the plan.
 */
import { Gunzip, unzipSync } from 'fflate';
import { readBytes } from './runUpload';
import { SERVER_LIMITS, formatBytes } from './uploadPlan';

export class ArchiveError extends Error {
  constructor(message, code) {
    super(message);
    this.name = 'ArchiveError';
    this.code = code;
  }
}

export class ArchiveCancelled extends Error {
  constructor() {
    super('Cancelled');
    this.name = 'AbortError';
  }
}

/** 'zip' | 'tar' | 'tgz' for what can be unpacked, 'unsupported' for other archives, null for a plain file. */
export function archiveKind(name) {
  const lower = String(name || '').toLowerCase();
  if (lower.endsWith('.zip')) { return 'zip'; }
  if (lower.endsWith('.tar.gz') || lower.endsWith('.tgz')) { return 'tgz'; }
  if (lower.endsWith('.tar')) { return 'tar'; }
  if (/\.(rar|7z|tar\.bz2|tbz2?|tar\.xz|txz|tar\.zst|bz2|xz|gz|zst)$/.test(lower)) { return 'unsupported'; }
  return null;
}

export const SUPPORTED_ARCHIVES_HINT = 'Only .zip (or .tar.gz) archives can be imported';
export const ARCHIVE_ACCEPT = '.zip,.tar.gz,.tgz,.tar,application/zip,application/x-zip-compressed,application/gzip,application/x-tar';

const nextTick = () => new Promise((resolve) => setTimeout(resolve, 0));
const throwIfAborted = (signal) => { if (signal?.aborted) { throw new ArchiveCancelled(); } };

const baseName = (relPath) => relPath.split('/').pop() || relPath;
const entry = (relPath, data) => ({ relPath, size: data.length, file: new File([data], baseName(relPath)) });

/** Refuses an archive that could not fit in one import (the server's limit on what one upload unpacks to). */
function checkBudget(totalBytes, fileCount, limits) {
  if (totalBytes > limits.maxExtractedBytes) {
    throw new ArchiveError(`The archive unpacks to more than ${formatBytes(limits.maxExtractedBytes)}, the most one import may hold.`, 'too_large');
  }
  if (fileCount > limits.maxFiles) {
    throw new ArchiveError(`The archive has more than ${limits.maxFiles} files, the most one import may hold.`, 'too_many_files');
  }
}

/**
 * Names of the zip's entries stored as links (made on Unix: S_IFLNK in the upper half of the external attributes),
 * read from the central directory. fflate does not say; vfs-service skips them too, but the archive it receives is
 * the one repacked here, where a link would otherwise arrive as a file holding its target.
 */
export function zipSymlinkNames(bytes) {
  const names = new Set();
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  let end = -1;
  for (let at = bytes.length - 22; at >= Math.max(0, bytes.length - 65557); at -= 1) {
    if (view.getUint32(at, true) === 0x06054b50) { end = at; break; }
  }
  if (end < 0) { return names; }
  const count = view.getUint16(end + 10, true);
  let at = view.getUint32(end + 16, true);
  for (let index = 0; index < count && at + 46 <= bytes.length; index += 1) {
    if (view.getUint32(at, true) !== 0x02014b50) { break; }
    const madeBy = view.getUint16(at + 4, true) >> 8;
    const utf8 = (view.getUint16(at + 8, true) & 0x800) !== 0;
    const nameLength = view.getUint16(at + 28, true);
    const extraLength = view.getUint16(at + 30, true);
    const commentLength = view.getUint16(at + 32, true);
    const mode = (view.getUint32(at + 38, true) >>> 16) & 0o170000;
    if (madeBy === 3 && mode === 0o120000) {
      names.add(new TextDecoder(utf8 ? 'utf-8' : 'latin1').decode(bytes.subarray(at + 46, at + 46 + nameLength)));
    }
    at += 46 + nameLength + extraLength + commentLength;
  }
  return names;
}

async function readZip(bytes, { signal, onProgress, skip, limits }) {
  const links = zipSymlinkNames(bytes);
  const listed = [];
  try {
    unzipSync(bytes, { filter: (info) => { listed.push(info); return false; } });
  } catch (error) {
    throw new ArchiveError(`This zip could not be read (${error?.message || 'damaged archive'}).`, 'unreadable');
  }
  const skipped = [];
  const wanted = [];
  let total = 0;
  for (const info of listed) {
    if (info.name.endsWith('/')) { continue; }
    if (links.has(info.name)) { continue; }
    if (skip?.(info.name)) { skipped.push(info.name); continue; }
    wanted.push(info);
    total += info.originalSize || 0;
  }
  checkBudget(total, wanted.length, limits);

  const files = [];
  let done = 0;
  for (let start = 0; start < wanted.length;) {
    throwIfAborted(signal);
    // A batch: up to 200 entries or 8 MB unpacked, then the page breathes.
    const names = new Set();
    let batchBytes = 0;
    while (start < wanted.length && (names.size === 0 || (names.size < 200 && batchBytes + (wanted[start].originalSize || 0) <= 8 * 1048576))) {
      names.add(wanted[start].name);
      batchBytes += wanted[start].originalSize || 0;
      start += 1;
    }
    let unpacked;
    try {
      unpacked = unzipSync(bytes, { filter: (info) => names.has(info.name) });
    } catch (error) {
      throw new ArchiveError(`This zip could not be unpacked (${error?.message || 'damaged archive'}). Encrypted zips are not supported.`, 'unreadable');
    }
    for (const [name, data] of Object.entries(unpacked)) { files.push(entry(name, data)); }
    done += batchBytes;
    onProgress?.({ phase: 'unpacking', done, total });
    // eslint-disable-next-line no-await-in-loop
    await nextTick();
  }
  return { files, skipped, links: [...links] };
}

async function gunzip(bytes, { signal, onProgress, limits }) {
  const chunks = [];
  let size = 0;
  let failure = null;
  const stream = new Gunzip((data) => {
    size += data.length;
    if (size > limits.maxExtractedBytes * 2) { failure = failure || new ArchiveError(`The archive unpacks to more than ${formatBytes(limits.maxExtractedBytes)}, the most one import may hold.`, 'too_large'); return; }
    chunks.push(data);
  });
  const step = 1048576;
  for (let offset = 0; offset < bytes.length; offset += step) {
    throwIfAborted(signal);
    try {
      stream.push(bytes.subarray(offset, offset + step), offset + step >= bytes.length);
    } catch (error) {
      throw new ArchiveError(`This .tar.gz could not be read (${error?.message || 'damaged archive'}).`, 'unreadable');
    }
    if (failure) { throw failure; }
    onProgress?.({ phase: 'unpacking', done: Math.min(offset + step, bytes.length), total: bytes.length });
    // eslint-disable-next-line no-await-in-loop
    await nextTick();
  }
  const out = new Uint8Array(size);
  let at = 0;
  for (const chunk of chunks) { out.set(chunk, at); at += chunk.length; }
  return out;
}

const decoder = new TextDecoder();
const cString = (bytes) => {
  const end = bytes.indexOf(0);
  return decoder.decode(end === -1 ? bytes : bytes.subarray(0, end));
};

/** A tar size/number field: octal text, or base-256 when the high bit is set (files over 8 GB). */
function tarNumber(field) {
  if (field[0] & 0x80) {
    let value = field[0] & 0x7f;
    for (let index = 1; index < field.length; index += 1) { value = value * 256 + field[index]; }
    return value;
  }
  const text = cString(field).trim();
  return text ? parseInt(text, 8) : 0;
}

/** `path` of a pax extended header (`<len> path=<value>\n` records). */
function paxPath(data) {
  const text = decoder.decode(data);
  let at = 0;
  let found = null;
  while (at < text.length) {
    const space = text.indexOf(' ', at);
    if (space === -1) { break; }
    const length = parseInt(text.slice(at, space), 10);
    if (!length) { break; }
    const record = text.slice(space + 1, at + length - 1);
    const equals = record.indexOf('=');
    if (record.slice(0, equals) === 'path') { found = record.slice(equals + 1); }
    at += length;
  }
  return found;
}

/**
 * The regular files of a tar (ustar, GNU long names, pax paths). Links, devices, fifos are left out (`skipped` with
 * reason 'link'); only types 0, '\0' and 7 (contiguous) are files.
 */
export async function readTar(bytes, { signal, onProgress, skip, limits = SERVER_LIMITS } = {}) {
  const files = [];
  const skipped = [];
  let offset = 0;
  let longName = null;
  let pax = null;
  let total = 0;
  let since = 0;
  while (offset + 512 <= bytes.length) {
    const header = bytes.subarray(offset, offset + 512);
    if (header.every((byte) => byte === 0)) { break; }
    const size = tarNumber(header.subarray(124, 136));
    const type = String.fromCharCode(header[156] || 48);
    const magic = cString(header.subarray(257, 263));
    const prefix = magic.startsWith('ustar') ? cString(header.subarray(345, 500)) : '';
    const dataStart = offset + 512;
    if (dataStart + size > bytes.length) { throw new ArchiveError('This tar archive is cut short (damaged or incomplete).', 'unreadable'); }
    const data = bytes.subarray(dataStart, dataStart + size);
    offset = dataStart + Math.ceil(size / 512) * 512;

    if (type === 'L') { longName = cString(data); continue; }
    if (type === 'x') { pax = paxPath(data) || pax; continue; }
    if (type === 'g') { continue; }
    const name = pax || longName || (prefix ? `${prefix}/${cString(header.subarray(0, 100))}` : cString(header.subarray(0, 100)));
    pax = null;
    longName = null;
    if (type === '5' || name.endsWith('/')) { continue; }
    if (!['0', '7', '\0'].includes(type)) { skipped.push({ relPath: name, reason: 'link' }); continue; }
    if (skip?.(name)) { skipped.push({ relPath: name, reason: 'skipped' }); continue; }
    total += size;
    checkBudget(total, files.length + 1, limits);
    files.push(entry(name.replace(/^\.\//, ''), data.slice()));
    since += size;
    if (since > 8 * 1048576) {
      since = 0;
      throwIfAborted(signal);
      onProgress?.({ phase: 'unpacking', done: offset, total: bytes.length });
      // eslint-disable-next-line no-await-in-loop
      await nextTick();
    }
  }
  return { files, skipped: skipped.map((line) => line.relPath), links: skipped.filter((line) => line.reason === 'link').map((line) => line.relPath) };
}

/**
 * @param {File|Blob} archive the picked or dropped archive (`name` tells its kind)
 * @param {object} [options]
 * @param {AbortSignal} [options.signal] Cancel
 * @param {(state: {phase: 'reading'|'unpacking', done: number, total: number}) => void} [options.onProgress]
 * @param {(relPath: string) => boolean} [options.skip] entries never decompressed (node_modules, .git, build output)
 * @returns {Promise<{files: Array<{file: File, relPath: string, size: number}>, skipped: string[], emptyFolders: string[], unreadable: Array}>}
 */
export async function readArchive(archive, { signal, onProgress, skip, limits = SERVER_LIMITS, name } = {}) {
  const kind = archiveKind(name || archive?.name);
  if (!kind || kind === 'unsupported') {
    throw new ArchiveError(`${SUPPORTED_ARCHIVES_HINT}: ${name || archive?.name || 'this file'} is not one.`, 'unsupported');
  }
  if (archive.size > limits.maxArchiveBytes) {
    throw new ArchiveError(`${formatBytes(archive.size)} is more than ${formatBytes(limits.maxArchiveBytes)}, the largest archive one import takes.`, 'too_large');
  }
  throwIfAborted(signal);
  onProgress?.({ phase: 'reading', done: 0, total: archive.size });
  const bytes = await readBytes(archive);
  throwIfAborted(signal);
  onProgress?.({ phase: 'reading', done: archive.size, total: archive.size });
  let result;
  if (kind === 'zip') {
    result = await readZip(bytes, { signal, onProgress, skip, limits });
  } else {
    const tarBytes = kind === 'tgz' ? await gunzip(bytes, { signal, onProgress, limits }) : bytes;
    result = await readTar(tarBytes, { signal, onProgress, skip, limits });
  }
  throwIfAborted(signal);
  return { files: result.files, skipped: result.skipped, emptyFolders: [], unreadable: (result.links || []).map((relPath) => ({ relPath, error: 'links are not unpacked' })) };
}

/** The archives (zip, tar, tar.gz) among the files dropped or picked into the tree. */
export function unpackableArchives(collected) {
  return (collected?.files || []).filter((entry) => ['zip', 'tar', 'tgz'].includes(archiveKind(entry.relPath)));
}

/**
 * "Unpack here": every archive among `collected.files` replaced by its files, placed in the archive's own folder
 * (`a/site.zip` holding `index.html` gives `a/index.html`). The other files are kept as they are.
 */
export async function unpackArchivesIn(collected, { signal, onProgress } = {}) {
  const archives = new Set(unpackableArchives(collected));
  const files = [];
  const unreadable = [...(collected.unreadable || [])];
  let index = 0;
  for (const entry of collected.files) {
    if (!archives.has(entry)) { files.push(entry); continue; }
    index += 1;
    const folder = entry.relPath.split('/').slice(0, -1).join('/');
    // eslint-disable-next-line no-await-in-loop
    const unpacked = await readArchive(entry.file, { name: entry.relPath, signal, onProgress: (state) => onProgress?.({ ...state, archive: entry.relPath, index, count: archives.size }) });
    for (const inner of unpacked.files) { files.push({ ...inner, relPath: folder ? `${folder}/${inner.relPath}` : inner.relPath }); }
    unreadable.push(...unpacked.unreadable.map((line) => ({ ...line, relPath: folder ? `${folder}/${line.relPath}` : line.relPath })));
  }
  return { ...collected, files, unreadable };
}
